import { useI18n } from 'vue-i18n'

import {
  type InboxCopySource,
  inboxBodyKey,
  inboxCopyArgs,
  inboxCopyPlural,
  inboxLineKey,
  inboxNoteLine,
  inboxNoteSubject,
  inboxNoteText,
  inboxSubjectKey,
  inboxVerbKey,
  inboxVerbSecondaryKey,
} from './inbox-copy'
import { useFormatters } from '@/composables/useFormatters'

/** The sentences of a message, each filled from its typed params. */
export interface InboxCopy {
  subject: (source: InboxCopySource) => string
  line: (source: InboxCopySource) => string
  body: (source: InboxCopySource) => string[]
  verb: (source: InboxCopySource) => string
  verbSecondary: (source: InboxCopySource) => string | null
}

/** Fills the catalogue templates of a message, or of a thread's newest message, with its params. */
export function useInboxCopy(): InboxCopy {
  const { t, tm } = useI18n()
  const { formatDecimal, formatFixed } = useFormatters()
  const format = { t, formatDecimal, formatFixed }

  function fill(key: string, source: InboxCopySource): string {
    const plural = inboxCopyPlural(source)
    const args = inboxCopyArgs(source, format)
    return plural === undefined ? t(key, args) : t(key, args, plural)
  }

  function subject(source: InboxCopySource): string {
    const key = inboxSubjectKey(source)
    return key === null ? inboxNoteSubject(inboxNoteText(source)) : fill(key, source)
  }

  function line(source: InboxCopySource): string {
    const key = inboxLineKey(source)
    return key === null ? inboxNoteLine(inboxNoteText(source)) : fill(key, source)
  }

  function body(source: InboxCopySource): string[] {
    const key = inboxBodyKey(source)
    if (key === null) return [inboxNoteText(source)]
    const paragraphs = tm(key) as unknown[]
    return paragraphs.map((_, index) => fill(`${key}[${index}]`, source))
  }

  function verb(source: InboxCopySource): string {
    return t(inboxVerbKey(source))
  }

  function verbSecondary(source: InboxCopySource): string | null {
    const key = inboxVerbSecondaryKey(source)
    return key === null ? null : t(key)
  }

  return { subject, line, body, verb, verbSecondary }
}
